import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { to: "/", label: "首页", end: true },
  { to: "/category/movie", label: "电影", end: false },
  { to: "/category/tv", label: "电视剧", end: false },
  { to: "/news", label: "资讯", end: false },
  { to: "/about", label: "关于我们", end: false },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
      isActive
        ? "text-primary-700 font-medium"
        : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors ${
        scrolled
          ? "bg-background-50/95 border-background-200 backdrop-blur"
          : "bg-background-50/80 border-background-100 backdrop-blur"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 mr-4 shrink-0 group">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-lg group-hover:bg-primary-600 transition-colors">
              <i className="ri-film-line"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-semibold text-lg text-foreground-950 tracking-wide">
                蜜桃成熟
              </span>
              <span className="hidden sm:block text-[10px] text-foreground-500 mt-0.5 font-label tracking-widest">
                PEACH CINEMA
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={submitSearch}
              className="hidden md:flex items-center gap-2 bg-background-100 border border-background-200 rounded-full pl-3 pr-1 py-1 focus-within:border-primary-400 transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center text-foreground-500 text-sm">
                <i className="ri-search-line"></i>
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视作品、资讯"
                className="bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400 w-36 lg:w-48"
              />
              <button
                type="submit"
                className="px-3 py-1.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm whitespace-nowrap transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="展开导航"
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-200 text-foreground-800 text-xl cursor-pointer hover:border-primary-300 transition-colors"
            >
              <i className={menuOpen ? "ri-close-line" : "ri-menu-line"}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-rise-in">
          <form
            onSubmit={submitSearch}
            className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-full pl-3 pr-1 py-1 mb-3"
          >
            <span className="w-5 h-5 flex items-center justify-center text-foreground-500 text-sm">
              <i className="ri-search-line"></i>
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视作品、资讯"
              className="bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400 flex-1 min-w-0"
            />
            <button
              type="submit"
              className="px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-sm text-center border transition-colors ${
                    isActive
                      ? "bg-primary-100 border-primary-200 text-primary-700 font-medium"
                      : "bg-background-100 border-background-200 text-foreground-700"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}